import { requireSupabase } from "@/lib/supabaseClient";
import { guard } from "@/lib/errors";

export const profileService = {
  getById: (id) =>
    guard("profileService.getById", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase.from("profiles").select("*").eq("id", id).maybeSingle();
      if (error) throw error;
      return data;
    }),

  getByIds: (ids = []) =>
    guard("profileService.getByIds", async () => {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (unique.length === 0) return [];
      const supabase = requireSupabase();
      const { data, error } = await supabase.from("profiles").select("*").in("id", unique);
      if (error) throw error;
      return data || [];
    }),

  updateProfile: (id, patch) =>
    guard("profileService.updateProfile", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("profiles")
        .update(patch)
        .eq("id", id)
        .select()
        .maybeSingle();
      if (error) throw error;
      return data;
    }),

  // Public profile lookup by username (case-insensitive).
  getByUsername: (username) =>
    guard("profileService.getByUsername", async () => {
      if (!username) return null;
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .ilike("username", username)
        .maybeSingle();
      if (error) throw error;
      return data;
    }),

  // In-place account upgrade — same account, only the role (+ agent shell) changes.
  upgradeRole: (id, role) =>
    guard("profileService.upgradeRole", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("profiles").update({ role }).eq("id", id).select().maybeSingle();
      if (error) throw error;
      // Ensure a matching pro-profile row exists for agent/developer roles.
      if (role === "agent") {
        await supabase.from("agents").upsert({ id }, { onConflict: "id" });
      } else if (role === "developer") {
        await supabase.from("developers").upsert({ id, company_name: data?.full_name || "My Company" }, { onConflict: "id" });
      }
      return data;
    }),

  // Agent professional profile (1:1 with profile id).
  getAgent: (id) =>
    guard("profileService.getAgent", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase.from("agents").select("*").eq("id", id).maybeSingle();
      if (error) throw error;
      return data;
    }),

  saveAgent: (id, patch) =>
    guard("profileService.saveAgent", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("agents").upsert({ id, ...patch }, { onConflict: "id" }).select().maybeSingle();
      if (error) throw error;
      return data;
    }),
};

// Fields that count toward profile completion (0–100).
export const PROFILE_COMPLETION_FIELDS = [
  "full_name", "username", "bio", "avatar_url", "cover_url", "phone", "whatsapp",
  "website", "office_address",
];
export function computeCompletion(profile) {
  if (!profile) return 0;
  let filled = 0;
  for (const f of PROFILE_COMPLETION_FIELDS) {
    const v = profile[f];
    if (Array.isArray(v) ? v.length : v) filled += 1;
  }
  if (Array.isArray(profile.languages) && profile.languages.length) filled += 1;
  const total = PROFILE_COMPLETION_FIELDS.length + 1;
  return Math.round((filled / total) * 100);
}

// Minimum profile requirements before a property may be PUBLISHED (drafts always allowed).
export const PUBLISH_REQUIREMENTS = [
  { key: "full_name", label: "Full name" },
  { key: "avatar_url", label: "Profile photo" },
  { key: "phone", label: "Phone number" },
  { key: "office_address", label: "Location" },
  { key: "bio", label: "Bio / About" },
];
export function publishReadiness(profile) {
  const missing = PUBLISH_REQUIREMENTS.filter((r) => !(profile?.[r.key] && String(profile[r.key]).trim()));
  return { ok: missing.length === 0, missing };
}
